// Runtime "View" switcher for a transaction shown as a list: lets the viewer
// swap, without touching the saved config, between the interfaces the embed
// system implements for the same transaction (saved-records list, multi-row
// spreadsheet entry, list + popup form). Extracted from the designer's
// EmbedListInterfaceHost so both the designer surfaces and the standalone
// runtime render the identical control.

import { LayoutGrid } from 'lucide-react'

export type EmbedListView = 'list' | 'tabular' | 'form-input' | 'compact' | 'simple' | 'process' | 'document'

const VIEW_OPTIONS: { value: EmbedListView; label: string }[] = [
  { value: 'list', label: 'List' },
  { value: 'tabular', label: 'Spread sheet' },
  { value: 'form-input', label: 'Form input' },
  { value: 'compact', label: 'Compact' },
  { value: 'simple', label: 'Simple' },
  // The PROCESS this object sits in, drawn as its diagram. Offered only where
  // a host resolves that the object is placed on a Process Flow — see
  // TxnScreen — so an application that draws none never sees it.
  { value: 'process', label: 'Process' },
  // The transaction filled in as a laid-out DOCUMENT (obj_type `E`). Offered
  // only where a host resolves that a Document Form is anchored on this object
  // — see TxnScreen — so a transaction nobody has given a document face never
  // sees it. Same conditional shape as 'process', deliberately: a switcher must
  // never offer a view its own host cannot render.
  { value: 'document', label: 'Document' },
]

// ── Per-surface allowlists ────────────────────────────────────────────────
// Every call site passes one of these explicitly: a switcher must never offer
// a view its own host cannot render. (`options` omitted still means "all", but
// no call site relies on that.)

/** The four views the smart-page EMBED hosts implement (the designer's
 *  EmbedListInterfaceHost and the deployed runtime's EmbedView). 'simple' is a
 *  <TxnScreen> record-editor layout with no embed host of its own — a smart
 *  page that wants it authors the equivalent `single-classic` capture template
 *  instead, so it is deliberately absent here. */
export const EMBED_INTERFACE_VIEWS: EmbedListView[] = [
  'list', 'tabular', 'form-input', 'compact',
]

/** <TxnScreen> with host-supplied alternate capture interfaces. */
export const TXN_SCREEN_VIEWS: EmbedListView[] = [
  'list', 'tabular', 'form-input', 'compact', 'simple',
]

/** Every view this switcher knows — the vocabulary a remembered choice is
 *  validated against (see TxnScreen's view memory), never an offer list. */
export const ALL_TXN_VIEWS: EmbedListView[] = VIEW_OPTIONS.map((o) => o.value)

/** <TxnScreen> without them — list / compact / simple are all built into the
 *  screen itself, so a host that supplies no capture slots still offers them. */
export const TXN_SCREEN_BUILTIN_VIEWS: EmbedListView[] = ['list', 'compact', 'simple']

// Compact toolbar-styled <select>. Rendered inside each view's toolbar
// right-slot; styling mirrors the other GridToolbar controls. Exported so
// the standalone transaction preview (LivePreviewTab) can host the same
// switcher in its own list toolbar. `options` restricts the offered views
// (e.g. a surface that only implements list + compact); omitted = all four.
export function InterfaceSwitcher({
  value, onChange, options,
}: {
  value: EmbedListView
  onChange: (v: EmbedListView) => void
  options?: EmbedListView[]
}) {
  const opts = options
    ? VIEW_OPTIONS.filter(o => options.includes(o.value))
    : VIEW_OPTIONS
  return (
    <label
      title="Switch how this transaction is shown (view only — not saved)"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        fontSize: '0.8425rem', color: 'var(--color-text-secondary)',
        cursor: 'pointer',
      }}
    >
      <LayoutGrid size={14} />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as EmbedListView)}
        // Stop the change/click bubbling so hosting toolbars (which may
        // treat row/area clicks as selection) don't react to it.
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-bg)',
          color: 'var(--color-text)',
          border: '1px solid var(--color-border)',
          borderRadius: 6,
          // Match the search input's height so the toolbar row aligns.
          height: 28, boxSizing: 'border-box',
          padding: '0 8px',
          fontSize: '0.8425rem',
          cursor: 'pointer',
          outline: 'none',
        }}
      >
        {opts.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  )
}
